// F11, opening the portal with no signal. The phone keeps the last sign-in that was checked online,
// until the moment the server's own session would end, and lets the portal open as that person when
// the server cannot be reached. A real "signed out" from the server still means signing in (review).
import type { Me } from '@/api/types.ts';

const KEY = 'meridian-portal-me';
/** The server's absolute session lifetime, for a session that did not say when it ends. */
const FALLBACK_MS = 7 * 24 * 60 * 60 * 1000;

type Saved = { me: Me; until: number };

function read(): Saved | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Saved) : null;
  } catch {
    return null;
  }
}

/**
 * After every check that reached the server. When someone else signed in than last time, what the
 * last person opened is cleared first, so it can never be shown to the new one.
 */
export async function rememberSignedIn(me: Me): Promise<void> {
  const previous = read();
  if (previous && previous.me.user.id !== me.user.id) await forgetSavedLists();
  const until = me.sessionEndsAt ? Date.parse(me.sessionEndsAt) : Date.now() + FALLBACK_MS;
  try {
    localStorage.setItem(KEY, JSON.stringify({ me, until } satisfies Saved));
  } catch {
    // Storage blocked (a private window): the portal still works, just not offline from cold.
  }
}

/** Who to open the portal as when the server cannot be reached, or null to send them to sign in. */
export function lastSignedIn(): Me | null {
  const saved = read();
  if (!saved || Date.now() > saved.until) return null;
  return saved.me;
}

/** The id of whoever this phone last saw signed in, for keeping queued changes apart. */
export const lastUserId = () => read()?.me.user.id ?? null;

/**
 * Signing out: this phone forgets who it was and the lists they opened. Changes they made offline
 * and have not sent stay queued under their name and go out when they next sign in here (review Q8).
 */
export async function forgetSignedIn(): Promise<void> {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing stored.
  }
  await forgetSavedLists();
}

/** The worker's copies of the person's data (sw-template.js); the app itself stays cached. */
async function forgetSavedLists(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    for (const name of await caches.keys()) {
      if (!name.startsWith('meridian-portal-')) continue;
      const cache = await caches.open(name);
      for (const request of await cache.keys()) {
        if (new URL(request.url).pathname.startsWith('/api/')) await cache.delete(request);
      }
    }
  } catch {
    // Storage blocked or never used.
  }
}
